import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  makeStyles,
  Box,
  Divider,
} from "@material-ui/core";
import Timeline from "@material-ui/lab/Timeline";
import TimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import TimelineConnector from "@material-ui/lab/TimelineConnector";
import TimelineContent from "@material-ui/lab/TimelineContent";
import TimelineOppositeContent from "@material-ui/lab/TimelineOppositeContent";
import TimelineDot from "@material-ui/lab/TimelineDot";
import StarIcon from "@mui/icons-material/Star";
import BuildIcon from "@mui/icons-material/Build";
import UpdateIcon from "@mui/icons-material/Update";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    paddingTop: 0,
    backgroundColor: theme.palette.type === "dark" ? "#1a1c2e" : "#fcfcfc",
  },
  title: {
    backgroundColor: theme.palette.type === "dark" ? "#1a1c2e" : "#fcfcfc",
    color: theme.palette.primary.main,
    fontWeight: "bold",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
  },
  timelineContent: {
    padding: "12px 16px",
    backgroundColor: theme.palette.type === "dark" ? "#22253a" : "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    marginBottom: "16px",
  },
  versionTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontWeight: 600,
    marginBottom: "8px",
  },
  dateText: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "6px",
    fontSize: "0.95rem",
  },
  featureIcon: {
    marginRight: "8px",
    marginTop: "2px",
    fontSize: "1.1rem",
  },
  featureNew: { color: theme.palette.success.main },
  featureFix: { color: theme.palette.warning.main },
  featureUpdate: { color: theme.palette.info.main },
  sloganContainer: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    paddingTop: "12px",
  },
  sloganText: {
    fontWeight: "bold",
    fontSize: "1rem",
    color: theme.palette.primary.main,
    textAlign: "right",
    lineHeight: 1.3,
  },
  legendContainer: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginLeft: "auto",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    "& svg": {
      fontSize: "1.2rem",
      marginRight: "4px",
    },
  },
}));

const changelogData = [
  {
    version: "v3.0.25",
    date: "28/10/2026",
    slogan: "⚡ Mensageria WhatsApp Plus Estabilizada & Sincronização Unificada de ACK",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "fix", text: "Fim das Mensagens Duplicadas no Plus: eliminada a duplicação concorrente no envio pelo canal WACALLS via índice único parcial e migração transacional com deduplicação de histórico." },
      { type: "fix", text: "Conversas Internas Entre Conexões: suporte completo ao mesmo wid em tickets distintos quando duas conexões do CRM dialogam, sem sequestro de dados ou sumiço de mensagens." },
      { type: "new", text: "Sincronização Unificada de ACK (Checks): novo UpdateMessageAckService com lock e transação atômica atualizando os checks cinzas e azuis simultaneamente em todos os tickets via Socket.IO." },
      { type: "fix", text: "Auto-Incremento no Model Message: correção de constraint not-null no PostgreSQL garantindo persistência consistente de novas mensagens." },
      { type: "update", text: "Trava de Segurança no Deploy: script de deploy com verificação em tempo real do índice único no PostgreSQL abortando com exit 1 antes do restart se o banco estiver desprotegido." }
    ]
  },
  {
    version: "v3.0.24",
    date: "25/10/2026",
    slogan: "🛡️ Toggles Mestres, Placeholders Nativos & Push Android Wake-Up",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Disjuntores Mestres nas Opções: toggles de chamadas, transferência, saudação e fila agora funcionam como chaves soberanas que cortam 100% o envio de mensagens quando desativados." },
      { type: "new", text: "Textos Padrão como Placeholders: eliminada a gravação forçada de textos no banco de dados; as frases sugeridas agora aparecem em cinza claro (placeholders) sem ocupar espaço no banco." },
      { type: "new", text: "Push Notifications no Android com Tela Apagada: prioridade alta (urgency high) e wake-up no modo Doze com botão de teste individual por aparelho em Configurações > Opções." },
      { type: "fix", text: "Blindagem de Chamadas no Backend: checagem defensiva de ativação e de conteúdo real em HandleWacallsCall e wbotMonitor, eliminando falsos disparos e duplicidades." },
      { type: "fix", text: "Controle em 3 Camadas do Catálogo: blindagem visual, de rotas diretas e de API garantindo que planos sem o módulo useProducts não acessem categorias ou vendas." },
      { type: "update", text: "Resiliência Visual: tratamento com reversão automática e alertas toast informativos ao alternar configurações gerais." }
    ]
  },
  {
    version: "v3.0.23",
    date: "23/10/2026",
    slogan: "📊 Relatórios de Vendas, Carrinho da Proposta & Upload Seguro",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Central de Relatórios de Vendas: dashboard comercial com KPIs de faturamento, ticket médio, conversão, pipeline e exportação Excel (.xlsx)." },
      { type: "new", text: "Proposta Comercial Unificada: botão 'Negociar Todos' e fechamento da proposta completa com baixa atômica de estoque em lote." },
      { type: "new", text: "Validação Inteligente de Uploads: proteção em 3 camadas com limites automáticos (100MB docs, 16MB vídeos/áudios/imagens) e avisos descritivos." },
      { type: "new", text: "Controle por Plano (useProducts): ativação ou desativação do Catálogo de Produtos e Serviços por plano da empresa." },
      { type: "fix", text: "Filtros de Atendimentos: correção do erro searchTimeout nos seletores de usuários, tags, conexões e status na listagem de tickets." },
      { type: "update", text: "Venda no Cartão Parcelado: fechamento direto de 1x a 12x com quitação na maquininha sem gerar promissórias desnecessárias." }
    ]
  },
  {
    version: "v3.0.22",
    date: "20/10/2026",
    slogan: "⚡ Catálogo de Alta Performance, Scroll Infinito & Trava de Esgotados",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Modal de Catálogo Compacto: lista de alta densidade (~64px) com até 10 produtos por tela e miniatura integrada." },
      { type: "new", text: "Scroll Infinito Contínuo: carregamento suave em lotes de 20 produtos sem necessidade de paginação numérica." },
      { type: "fix", text: "Bloqueio Completo de Esgotados: trava preventiva no atalho '#', no modal e no servidor para itens sem estoque físico." },
      { type: "update", text: "Atalho '#' Enriquecido: badge de estoque em tempo real, cores do tema Whitelabel e checkbox inline para envio de galeria." },
      { type: "fix", text: "Dropdown de Categorias: exibição imediata de 'Todas as Categorias' sem fundo branco e rótulo perfeitamente fixado." },
      { type: "update", text: "Acessibilidade: alertas visuais (toasts) com tipografia de alto contraste em preto negrito." }
    ]
  },
  {
    version: "v3.0.21",
    date: "20/10/2026",
    slogan: "📜 Notas Promissórias Eletrônicas & PWA Inteligente",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Notas Promissórias com Força de Título Executivo (Art. 784 CPC): emissão, parcelamento e assinatura digital com PIN OTP via WhatsApp." },
      { type: "new", text: "Laudo Pericial & Validação Pública: emissão de PDF mestre com QR Code e página pública de verificação com validade jurídica." },
      { type: "new", text: "Novo Sistema de Atualização do PWA: botão '[Atualizar Agora]' e limpeza atômica de cache para Desktop e Celular." },
      { type: "update", text: "Catálogo no Mobile & Placeholders: botão do catálogo adicionado ao menu de ações mobile e placeholders normalizados." },
      { type: "fix", text: "Notificações Push Blindadas: eliminação de notificações duplicadas no Windows e cancelamento automático no logout." }
    ]
  },
  {
    version: "v3.0.20",
    date: "12/10/2026",
    slogan: "🛒 Catálogo de Produtos integrado ao Chat de Atendimento",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Atalho '#' no chat: digite '#' para buscar produtos rapidamente por nome ou SKU e enviar com um clique." },
      { type: "new", text: "Modal de Catálogo no Chat: envie cards profissionais com fotos, visualize histórico de enviados e gerencie propostas comerciais do chamado." },
      { type: "update", text: "Envio inteligente para WhatsApp com foto principal, legenda detalhada e limite de segurança de 1024 caracteres." },
      { type: "new", text: "Baixa atômica de estoque em vendas e cancelamento/estorno de itens restrito com segurança a administradores." }
    ]
  },
  {
    version: "v3.0.19",
    date: "12/10/2026",
    slogan: "📦 Catálogo Geral de Produtos e Categorias Dinâmicas",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Gestão completa de Produtos & Serviços com suporte a múltiplas fotos, SKU automático e links de pagamento." },
      { type: "new", text: "Categorias com campos personalizados flexíveis para qualquer segmento de negócio (moda, veículos, imóveis, serviços)." },
      { type: "update", text: "Formatação monetária padronizada e controle de permissões por perfil de usuário." }
    ]
  },
  {
    version: "v3.0.18",
    date: "11/10/2026",
    slogan: "📱 Sessão Prolongada no PWA (30 Dias)",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Login contínuo no PWA para celulares: o aplicativo agora mantém sua sessão ativa por até 30 dias ininterruptos." },
      { type: "fix", text: "Fim da perda de sessão ao minimizar o app, bloquear a tela do aparelho ou alternar para outros aplicativos." },
      { type: "update", text: "Maior resiliência na inicialização em redes móveis com oscilação temporária de conexão." }
    ]
  },
  {
    version: "v3.0.17",
    date: "11/10/2026",
    slogan: "🔒 Permissões Aprimoradas e Estabilidade",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "fix", text: "Usuários sem permissão para ver chamados sem fila não recebem mais badges, notificações sonoras nem alertas indevidos." },
      { type: "fix", text: "Blindagem do painel de atendimentos contra travamentos visuais na listagem de tickets." },
      { type: "update", text: "Busca instantânea de atendentes no modal de transferência já a partir da primeira letra digitada." }
    ]
  },
  {
    version: "v3.0.16",
    date: "06/10/2026",
    slogan: "🛡️ Estabilidade e Tratamento de Erros",
    icon: <BuildIcon />,
    color: "primary",
    changes: [
      { type: "fix", text: "Correção definitiva da 'tela vermelha de erro' que piscava na tela quando a sessão do sistema expirava (erro 401)." },
      { type: "fix", text: "Ajuste na fila de requisições de autenticação, evitando o travamento do sistema em loading infinito ao processar atualizações." }
    ]
  },
  {
    version: "v3.0.15",
    date: "05/10/2026",
    slogan: "✨ Nova Comunicação de Boas-Vindas",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "A tela de autenticação foi renovada e agora conta com mensagens customizadas e novos destaques visuais." },
      { type: "new", text: "Melhorias estruturais na página inicial para oferecer uma recepção mais agradável e alinhada com a identidade da plataforma." }
    ]
  },
  {
    version: "v3.0.14",
    date: "05/10/2026",
    slogan: "✨ Melhorias Visuais na Autenticação",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Design modernizado para as telas de Login e Cadastro, oferecendo uma experiência mais premium e agradável." },
      { type: "update", text: "Refinamento nas animações e cores de destaque do Modal de Boas Vindas, agora mais fluidas." }
    ]
  },
  {
    version: "v3.0.13",
    date: "05/10/2026",
    slogan: "✨ Compartilhamento de Kanban por Fila",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Adicionada a capacidade de vincular Boards do Kanban a Filas de Atendimento específicas (substituindo a antiga opção 'Compartilhar com todos')." },
      { type: "new", text: "Usuários não-administradores agora visualizam e acessam apenas os Boards vinculados às suas próprias filas." },
      { type: "fix", text: "Usuários comuns não conseguem mais acessar configurações, arrastar ou deletar colunas/boards (Bloqueio visual de Drag and Drop)." },
      { type: "fix", text: "Prevenção de travamentos na interface de arrastar-e-soltar resolvendo a colisão de IDs de Cards e Colunas." }
    ]
  },
  {
    version: "v3.0.12",
    date: "26/09/2026",
    slogan: "✨ Layout Inteligente e Responsivo (Topbar)",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Nome do usuário adicionado ao lado do Avatar na Topbar com proteção de reticências (truncamento dinâmico)." },
      { type: "new", text: "Inclusão do Nome e Perfil do usuário no cabeçalho do menu dropdown, aprimorando a experiência em telas menores (onde a Topbar comprime o texto)." },
      { type: "fix", text: "Ajuste na responsividade da barra superior (CSS Flexbox) para garantir que ícones e avatares não sejam sobrepostos ou empurrados para fora em resoluções intermediárias (ex: 1280px)." }
    ]
  },
  {
    version: "v3.0.11",
    date: "26/09/2026",
    slogan: "✨ Melhorias de UI/UX (Centro de IA)",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "update", text: "Desambiguação da interface de Agentes: Renomeação das abas para Assistentes Virtuais, Biblioteca de Templates e Automação." },
      { type: "update", text: "Aprimoramentos nos formulários (PromptModal e SalesAgents) focando em clareza com nomes como 'Template de Personalidade'." },
      { type: "new", text: "Adição de Tooltips explicativas nos campos de Chave de Extração e Nome do Campo para orientar o usuário sem consumir espaço visual." },
      { type: "fix", text: "Correção visual no desktop que quebrava os títulos das abas em duas linhas e atualização das traduções no dicionário." }
    ]
  },
  {
    version: "v3.0.10",
    date: "26/09/2026",
    slogan: "🔒 Segurança e Permissões",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "fix", text: "Ocultação do botão 'Pipeline de Vendas' para usuários que possuem a permissão de visualização do Kanban desabilitada." },
      { type: "fix", text: "Bloqueio e redirecionamento de usuários sem permissão caso tentem acessar a URL do Kanban diretamente." }
    ]
  },
  {
    version: "v3.0.9",
    date: "24/09/2026",
    slogan: "⚡ Cache Automático e Notificações",
    icon: <UpdateIcon />,
    color: "secondary",
    changes: [
      { type: "new", text: "Implementação de sistema de Cache Busting (recarregamento automático) para prevenir problemas de navegadores mantendo versões desatualizadas do CRM." },
      { type: "new", text: "Notificação visual (alerta vermelho) no botão de versão para sinalizar instantaneamente novas atualizações no Changelog." }
    ]
  },
  {
    version: "v3.0.8",
    date: "24/09/2026",
    slogan: "🤖 Gestão Unificada de IA e Arquitetura",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Nova tela centralizada 'Gestão de IA' unificando Motores de IA (Prompts), Modelos de Partida e Agentes Comerciais em abas encadeadas." },
      { type: "new", text: "Matriz de Objeções: Configure a IA para aprender a contornar as principais objeções de clientes de forma inteligente." },
      { type: "new", text: "Instruções de Extração (Anti-Alucinação): Modelos rápidos integrados para qualificar leads extraindo dados valiosos da conversa com altíssima precisão." },
      { type: "update", text: "Reestruturação arquitetural profunda no React Router (Core) para unificar rotas legadas sob a via de acesso segura (/ai-center)." },
      { type: "update", text: "Refatoração de Menu Lateral e Header de Topo: limpeza de botões redundantes, consolidando tudo no hub de IA." },
      { type: "fix", text: "Adequação de regras de negócio: Proteção contra duplicação de funções de IA (evitando confusão entre Agente e Motor)." },
      { type: "new", text: "Otimização de design: Cores de abas vibrantes, flexbox embutido e ícones dinâmicos alinhados." }
    ]
  },
  {
    version: "v3.0.7",
    date: "23/09/2026",
    slogan: "🤖 Automação Inteligente de Reações no Sistema",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "update", text: "Correção e aprimoramento nas reações automáticas: agora o sistema consegue reagir perfeitamente tanto às mensagens vindas do lado do cliente quanto às enviadas de dentro do próprio painel." }
    ]
  },
  {
    version: "v3.0.6",
    date: "22/09/2026",
    slogan: "🤖 Automação Inteligente de Reações",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Motor de Regras para Reações Automáticas: agora você pode configurar múltiplas palavras-chave e grupos de emojis variados para a IA reagir automaticamente às mensagens do cliente!" },
      { type: "update", text: "Criador visual de regras de reação na tela de configurações." },
    ]
  },
  {
    version: "v3.0.5",
    date: "22/09/2026",
    slogan: "👍 Reações Nativas e Layout Otimizado",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Novo sistema nativo de reações com emojis incorporado perfeitamente no balão da mensagem (igual no WhatsApp original)." },
      { type: "fix", text: "Remoção de mensagens indesejadas de texto que poluíam o chat avisando sobre reações no Baileys." },
      { type: "fix", text: "Correção da ancoragem de popovers e modais (Menu de opções da mensagem)." },
    ]
  },
  {
    version: "v3.0.4",
    date: "22/09/2026",
    slogan: "📊 Kanban Fluido e Confiável",
    icon: <CheckCircleIcon />,
    color: "primary",
    changes: [
      { type: "new", text: "Atualização Otimista no Drag and Drop: cards movem instantaneamente sem atraso visual." },
      { type: "fix", text: "Remoção de pulos visuais do card passando pela coluna 'Em aberto'." },
      { type: "fix", text: "Coluna 'Em aberto' travada na 1ª posição para garantir fluxo de vendas adequado." },
      { type: "update", text: "ID das colunas agora é exibido de forma sutil na tela do Kanban." },
    ]
  },
  {
    version: "v3.0.3",
    date: "20/09/2026",
    slogan: "⚡ Sistema de Cache Automático",
    icon: <UpdateIcon />,
    color: "secondary",
    changes: [
      { type: "new", text: "Adicionado sistema de Cache Busting (recarregamento automático) ao atualizar o CRM." },
    ]
  },
  {
    version: "v3.0.2",
    date: "20/09/2026",
    slogan: "🌐 Internacionalização e Melhorias de UI",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "fix", text: "Tradução da tela de conexões (connections.noResults) corrigida para os 5 idiomas suportados." },
    ]
  },
  {
    version: "v3.0.1",
    date: "20/09/2026",
    slogan: "🛡️ Mais Estabilidade e Consistência",
    icon: <StarIcon />,
    color: "primary",
    changes: [
      { type: "fix", text: "Tratamento de erro 401 global (sessão expirada) para evitar tela vermelha no PWA." },
      { type: "new", text: "Adicionada opção 'Ocultar modal de encerramento' nas configurações da empresa." },
    ]
  },
  {
    version: "v3.0.0",
    date: "06/09/2026",
    slogan: "🚀 Fluxos Inteligentes e Gestão Visual",
    icon: <CheckCircleIcon />,
    color: "secondary",
    changes: [
      { type: "new", text: "Kanban nativo no FlowBuilder com nó de encerramento e escolha de coluna." },
      { type: "new", text: "Push Notifications automáticas (Auto-sync) para PWA/Mobile." },
      { type: "new", text: "Contador de conversas não lidas diretamente no ícone/badge do app." },
      { type: "fix", text: "Bug de reabertura duplicada ao fechar ticket via Baileys resolvido." },
      { type: "update", text: "Unificação da palavra-chave de saída em todos os canais (Meta, Baileys, Wacalls)." },
    ]
  },
  {
    version: "v2.9.8",
    date: "02/09/2026",
    slogan: "📱 Integrações Mais Poderosas",
    icon: <UpdateIcon />,
    color: "grey",
    changes: [
      { type: "new", text: "Suporte total a mídias (áudios, vídeos, documentos) na integração WhatsApp Plus (Wacalls)." },
      { type: "update", text: "Painéis de configuração do FlowBuilder modernizados." },
      { type: "update", text: "Página de relatórios aprimorada com gráficos avançados." },
      { type: "fix", text: "Correção de áudios OGG aparecendo com 0:00 no WhatsApp." },
    ]
  },
  {
    version: "v2.9.7",
    date: "01/09/2026",
    slogan: "💎 Mais Rápido, Seguro e Escalável",
    icon: <BuildIcon />,
    color: "grey",
    changes: [
      { type: "update", text: "Lançamento oficial da reestruturação CRM Moovein." },
      { type: "update", text: "Higienização completa da interface." },
      { type: "update", text: "Melhorias de infraestrutura, multi-tenant e segurança em IA." },
    ]
  }
];

const ChangelogModal = ({ open, onClose }) => {
  const classes = useStyles();

  const getIconForType = (type) => {
    switch (type) {
      case "new":
        return <StarIcon className={`${classes.featureIcon} ${classes.featureNew}`} />;
      case "fix":
        return <BuildIcon className={`${classes.featureIcon} ${classes.featureFix}`} />;
      case "update":
        return <UpdateIcon className={`${classes.featureIcon} ${classes.featureUpdate}`} />;
      default:
        return <CheckCircleIcon className={`${classes.featureIcon}`} />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle className={classes.title}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>Novidades e Atualizações</span>
          <Box className={classes.legendContainer}>
            <Box className={classes.legendItem}>
              <StarIcon className={classes.featureNew} /> Nova Funcionalidade
            </Box>
            <Box className={classes.legendItem}>
              <UpdateIcon className={classes.featureUpdate} /> Melhoria
            </Box>
            <Box className={classes.legendItem}>
              <BuildIcon className={classes.featureFix} /> Correção de Bug
            </Box>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Timeline style={{ paddingLeft: 0, paddingRight: 0 }}>
          {changelogData.map((release, index) => (
            <TimelineItem key={index}>
              <TimelineOppositeContent style={{ flex: 0.35, paddingLeft: 0, paddingRight: "24px" }}>
                <Box className={classes.sloganContainer}>
                  <Typography className={classes.sloganText}>
                    {release.slogan}
                  </Typography>
                </Box>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot color={release.color}>
                  {release.icon}
                </TimelineDot>
                {index < changelogData.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Box className={classes.timelineContent}>
                  <Box className={classes.versionTitle}>
                    <Typography variant="h6" color="primary">
                      {release.version}
                    </Typography>
                    <Typography variant="body2" className={classes.dateText}>
                      {release.date}
                    </Typography>
                  </Box>
                  <Divider style={{ marginBottom: "12px" }} />
                  {release.changes.map((change, idx) => (
                    <Box key={idx} className={classes.featureItem}>
                      {getIconForType(change.type)}
                      <Typography variant="body2">
                        {change.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </DialogContent>
      <DialogActions style={{ padding: "16px" }}>
        <Button onClick={onClose} color="primary" variant="contained" disableElevation>
          Entendi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangelogModal;
